import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseResource } from '../../core/abstractions/base.resource';
import { RequestService } from '../request.service';
import { ResourceConfig } from '../../core/models/resource.config';
import { UserModel } from '../../models/user.model';
import { AuthRequestModel } from '../../models/auth-request.model';

@Injectable({
    providedIn: 'root'
})
export class AuthResource extends BaseResource<UserModel> {

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        const requestService = inject(RequestService);

        super(
            new ResourceConfig<UserModel>({
                requestService,
                baseController: 'users/authenticate'
            })
        );
    }

    login(data: AuthRequestModel): Observable<UserModel> {
        return this.config.requestService.makePost<UserModel>(this.getUrl(''), data);
    }

    /**
     * Exchanges a still-valid refresh token for a fresh access token (and a
     * rotated refresh token) — Issue #6.
     *
     * Endpoint: POST /api/v2/users/authenticate/refresh
     * Body:     { refreshToken }
     * 200:      UserModel including a new `token` and rotated `refreshToken`
     * 401:      refresh token unknown / expired / revoked
     *
     * The endpoint is intentionally kept under the existing `users/authenticate`
     * controller so the URL is built through the same `ResourceConfig`.
     */
    refresh(refreshToken: string): Observable<UserModel> {
        return this.config.requestService.makePost<UserModel>(this.getUrl('refresh'), { refreshToken });
    }

    /**
     * Best-effort server-side invalidation of a refresh token on logout
     * (Issue #6). Callers must treat failures as non-fatal so logout never
     * blocks (e.g. offline).
     *
     * Endpoint: POST /api/v2/users/authenticate/revoke
     * Body:     { refreshToken }
     */
    revoke(refreshToken: string): Observable<void> {
        return this.config.requestService.makePost<void>(this.getUrl('revoke'), { refreshToken });
    }

    /**
     * Fetches the per-user IndexedDB encryption key (Issue #4).
     * Endpoint: GET /api/v2/auth/db-key -> { key: '<base64, 32 bytes>' }.
     * The key is JWT-protected and returned base64-encoded.
     */
    getDbKey(): Observable<string> {
        return this.config.requestService
            .makeGet<{ key: string }>('auth/db-key')
            .pipe(map(response => response.key));
    }
}
